import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAppConfig } from '@/hooks/use-app-config';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { useAuth } from '@/components/AuthProvider';
import { BookmarkPlus, Check } from 'lucide-react';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';

// Lazy load SmartPaywall to avoid loading Stripe/RevenueCat until needed
const SmartPaywall = lazy(() => 
  import('@/components/subscription/SmartPaywall').then(m => ({ default: m.SmartPaywall }))
);

interface SubscriptionPurchaseButtonProps {
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  onReadingListToggle?: () => void;
  isInReadingList?: boolean;
  isReadingListLoading?: boolean;
  onOpenAuthModal?: () => void;
}

export function SubscriptionPurchaseButton({ className, size = 'default', onReadingListToggle, isInReadingList, isReadingListLoading, onOpenAuthModal }: SubscriptionPurchaseButtonProps) {
  const [showPaywall, setShowPaywall] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { currency } = useCurrencyDetection();

  // Only show for subscription model and non-active subscriptions
  if (appConfig?.current_model !== 'subscription' || subscriptionStatus?.subscription_status === 'active') {
    return null;
  }

  const handleSubscribe = () => {
    if (!user) {
      if (onOpenAuthModal) {
        onOpenAuthModal();
      } else {
        navigate('/v2');
      }
      return;
    }
    // Open paywall directly
    setShowPaywall(true);
  };

  return (
    <>
      {showPaywall && (
        <Suspense fallback={null}>
          <SmartPaywall
            isOpen={showPaywall}
            onClose={() => setShowPaywall(false)}
            onPurchaseComplete={() => setShowPaywall(false)}
          />
        </Suspense>
      )}

      <div className="flex gap-2 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          {user && (
            <p className="text-white/80 text-[11px] text-center">
              from {currency.symbol}{((appConfig?.annual_price || 47.88) / 12).toFixed(2)} per month
            </p>
          )}
          
          <Button
            onClick={handleSubscribe}
            className={`min-w-[160px] md:min-w-[180px] h-10 md:h-12 px-5 md:px-10 bg-white text-black font-semibold text-sm md:text-base hover:bg-white/90 transition-all duration-200 rounded-[10px] ${className || ''}`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif'
            }}
            size="lg"
          >
            {!user ? "Get Started" : subscriptionStatus?.has_had_subscription ? "Renew Subscription" : "Subscribe Now"}
          </Button>
        </div>
        
        {onReadingListToggle && (
          <Button
            onClick={onReadingListToggle}
            disabled={isReadingListLoading}
            className="h-10 w-10 md:h-12 md:w-12 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20 font-semibold rounded-full transition-all duration-200 flex items-center justify-center flex-shrink-0 self-end"
          >
            {isReadingListLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
            ) : isInReadingList ? (
              <Check className="w-5 h-5" />
            ) : (
              <BookmarkPlus className="w-5 h-5" />
            )}
          </Button>
        )}
      </div>
    </>
  );
}
